import { FormEvent, useEffect, useRef, useState } from "react";
import { TableSkeleton } from "../../components/Loading";
import { Link } from "react-router-dom";
import { ExportButton } from "../../components/ExportButton";
import { useTranslation } from "react-i18next";
import {
  procurementContractsApi,
  procurementSuppliersApi,
  type ProcurementContract,
  type ProcurementContractStatus,
  type ProcurementSupplier,
} from "../../api/procurementContracts";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import { StatusBadge } from "./ProcurementContractsPage";
import { FulfilmentBar } from "../../components/FulfilmentBar";
import { Tag } from "../../components/primitives.jsx";
import { Pager } from "../../components/Pager";
import { usePage } from "../../lib/usePage";

const ALL_STATUSES: ProcurementContractStatus[] = ["DRAFT", "ACTIVE", "FULFILLED", "CANCELLED"];

/** Ista veličina strane koju server podrazumeva, da broj redova na ekranu i u adresi bude isti. */
const VELICINA_STRANE = 25;

export default function AdminProcurementContractsPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("section:procurement_contracts:write");
  const { t } = useTranslation();
  const toast = useToast();
  const [contracts, setContracts] = useState<ProcurementContract[] | null>(null);
  const [companies, setCompanies] = useState<ProcurementSupplier[]>([]);
  // The suppliers a new contract may be written for — the caller's write scope,
  // narrower than the filter's read scope for the otkup desk.
  const [actableCompanies, setActableCompanies] = useState<ProcurementSupplier[]>([]);
  const [statusFilter, setStatusFilter] = useState<ProcurementContractStatus | "">("");
  // Firma se prima iz adrese, pa veza sa kartice dobavljača stvarno suzi spisak. Bez ovoga
  // `?companyId=` bi stajao u adresi a ekran bi ga ignorisao — veza koja obećava filter koji
  // se ne primenjuje gora je od veze bez filtera.
  const [companyFilter, setCompanyFilter] = useState<string>(
    () => new URLSearchParams(window.location.search).get("companyId") ?? "",
  );
  // Strana dolazi sa servera, a broj pored stranicara je serverov ukupan: registar otkupnih ugovora
  // raste sa poslom (iz evidencije otkupa ulazi 580 zaključenih ugovora), pa spisak koji bi se
  // iscrtao odjednom sekao bi se u pregledaču i ne bi rekao od čega je odsečen.
  const [strana, postaviStranu] = usePage();
  const [ukupno, setUkupno] = useState(0);
  const [creating, setCreating] = useState(false);
  const [openContract, setOpenContract] = useState<ProcurementContract | null>(null);

  // Otkad strana dolazi sa servera, brzo kliktanje po stranicaru drži tri čitanja u vazduhu, a
  // odgovori ne stižu redom kojim su traženi. Bez broja izdanja pobedio bi onaj koji je poslednji
  // došao, pa i stari — isti čuvar koji `Registar` nosi.
  const izdanje = useRef(0);
  const [osvezava, setOsvezava] = useState(false);

  async function load() {
    const moje = (izdanje.current += 1);
    setOsvezava(true);
    try {
      // Suppliers come from the otkup-scoped endpoint: the OTKUP referent holds no
      // companies:view, and that endpoint already applies the otkup scopes.
      const [page, comps, actable] = await Promise.all([
        procurementContractsApi.list(
          { status: statusFilter || undefined, companyId: companyFilter || undefined },
          strana,
          VELICINA_STRANE,
        ),
        procurementSuppliersApi.list(),
        canWrite ? procurementSuppliersApi.list({ actable: true }) : Promise.resolve([]),
      ]);
      if (moje !== izdanje.current) return;
      setContracts(page.content);
      setUkupno(page.totalElements);
      setCompanies(comps);
      setActableCompanies(actable);
    } catch (err) {
      if (moje !== izdanje.current) return;
      toast.error((err as ApiError).message);
    } finally {
      if (moje === izdanje.current) setOsvezava(false);
    }
  }

  useEffect(() => { void load(); }, [statusFilter, companyFilter, canWrite, strana]);

  // A narrowed filter is answered from the first page. Left on page seven it would come back empty
  // and read as "no such contract".
  const prethodniFilter = useRef(`${statusFilter}|${companyFilter}`);
  useEffect(() => {
    const kljuc = `${statusFilter}|${companyFilter}`;
    if (prethodniFilter.current === kljuc) return;
    prethodniFilter.current = kljuc;
    postaviStranu(0);
  }, [statusFilter, companyFilter, postaviStranu]);

  // Exports go through the same filters as the table, so what you see is what
  // you download.
  const exportQuery = (() => {
    const p = new URLSearchParams();
    if (statusFilter) p.set("status", statusFilter);
    if (companyFilter) p.set("companyId", companyFilter);
    const q = p.toString();
    return q ? `?${q}` : "";
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.procurement.contracts.title")}</h1>
        <button type="button" onClick={() => setCreating(true)} style={primary} disabled={!canWrite || actableCompanies.length === 0}>
          {t("admin.procurement.contracts.newContract")}
        </button>
      </header>

      <div style={{ display: "flex", gap: 12 }}>
        <Select<string>
          value={companyFilter}
          onChange={setCompanyFilter}
          ariaLabel={t("admin.procurement.contracts.allCompanies")}
          style={{ flex: 1 }}
          options={[
            { value: "", label: t("admin.procurement.contracts.allCompanies") },
            ...companies.map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <Select<ProcurementContractStatus | "">
          value={statusFilter}
          onChange={setStatusFilter}
          ariaLabel={t("admin.procurement.contracts.allStatuses")}
          style={{ flex: 1 }}
          options={[
            { value: "", label: t("admin.procurement.contracts.allStatuses") },
            ...ALL_STATUSES.map((s) => ({ value: s, label: s })),
          ]}
        />
        <ExportButton
          path={`/exports/procurement-contracts.csv${exportQuery}`}
          label={t("procurement.contracts.list.exportCsv", "CSV")}
          filename="procurement-contracts.csv"
        />
        <ExportButton
          path={`/exports/procurement-contracts.xlsx${exportQuery}`}
          label={t("procurement.contracts.list.exportXlsx", "XLSX")}
          filename="procurement-contracts.xlsx"
        />
      </div>

      {!contracts ? (
        <TableSkeleton rows={6} cols={5} label={t("common.loading")} />
      ) : contracts.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.procurement.contracts.empty")}</p>
      ) : (
        <>
        <Pager page={strana} size={VELICINA_STRANE} total={ukupno} busy={osvezava} onPage={postaviStranu} />
        {/* Strana koja se čita zadržava zatečene redove i zatamni ih. Kostur umesto njih bio bi
            druga tvrdnja: da registra nema, umesto da se sledeća strana donosi. */}
        <table style={{ ...tbl, opacity: osvezava ? 0.55 : 1, transition: "opacity 120ms" }}>
          <thead>
            <tr>
              <th style={th}>{t("admin.procurement.contracts.col.reference")}</th>
              <th style={th}>{t("admin.procurement.contracts.col.company")}</th>
              <th style={th}>{t("admin.procurement.contracts.col.culture")}</th>
              <th style={th}>{t("admin.procurement.contracts.col.quantity")}</th>
              <th style={th}>{t("admin.procurement.contracts.col.harvestYear")}</th>
              <th style={th}>{t("procurement.contract.fulfilment", "Fulfilment")}</th>
              <th style={th}>{t("admin.procurement.contracts.col.status")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.id}>
                <td style={td}>
                  <Link to={`/procurement/contracts/${c.id}`} style={{ color: "inherit" }}>
                    <code>{c.referenceNumber}</code>
                  </Link>
                </td>
                <td style={td}>{c.companyName}</td>
                <td style={td}>{c.culture ?? "—"}</td>
                <td style={td}>{c.quantityT} t</td>
                <td style={td}>{c.harvestYear}</td>
                <td style={td}>
                  <FulfilmentBar
                    settledT={c.settledT}
                    inProgressT={c.inProgressT}
                    remainingT={c.remainingT}
                    fulfilmentPct={c.fulfilmentPct}
                  />
                </td>
                <td style={td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <StatusBadge status={c.status} />
                    {c.readyToClose && (
                      <span
                        title={t(
                          "procurement.contract.readyToCloseHint",
                          "Contracted quantity reached — the contract is closed by hand.",
                        )}
                      >
                        <Tag kind="warning" dot={undefined} style={undefined}>
                          {t("procurement.contract.readyToClose", "Ready to close")}
                        </Tag>
                      </span>
                    )}
                  </div>
                </td>
                <td style={td}>
                  <button type="button" onClick={() => setOpenContract(c)} style={linkBtn}>{t("admin.procurement.contracts.action.open")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager page={strana} size={VELICINA_STRANE} total={ukupno} busy={osvezava} onPage={postaviStranu} />
        </>
      )}

      {canWrite && creating && (
        <CreateModal
          companies={actableCompanies}
          onClose={() => setCreating(false)}
          onCreated={async () => { setCreating(false); await load(); }}
        />
      )}
      {openContract && (
        <ContractDetailModal
          contract={openContract}
          onClose={() => setOpenContract(null)}
          onChanged={async () => { await load(); }}
        />
      )}
    </div>
  );
}

function CreateModal({
  companies, onClose, onCreated,
}: {
  companies: ProcurementSupplier[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [companyId, setCompanyId] = useState(companies[0]?.id ?? "");
  const [reference, setReference] = useState("");
  const [culture, setCulture] = useState("");
  const [quantityT, setQuantityT] = useState("");
  const [pricePerT, setPricePerT] = useState("");
  const [harvestYear, setHarvestYear] = useState(String(new Date().getFullYear()));
  const [effective, setEffective] = useState("");
  const [expires, setExpires] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await procurementContractsApi.create({
        companyId,
        referenceNumber: reference,
        culture: culture || undefined,
        quantityT: Number(quantityT),
        pricePerT: pricePerT ? Number(pricePerT) : undefined,
        harvestYear: Number(harvestYear),
        effectiveDate: effective || undefined,
        expiresAt: expires || undefined,
      });
      toast.success(t("admin.procurement.contracts.toast.created"));
      await onCreated();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>{t("admin.procurement.contracts.modal.new")}</h2>
      <form onSubmit={handle} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label={t("admin.procurement.contracts.field.company")} required>
          <Select<string>
            value={companyId}
            onChange={setCompanyId}
            ariaLabel={t("admin.procurement.contracts.field.company")}
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
          />
        </Field>
        <Field label={t("admin.procurement.contracts.field.referenceNumber")} required>
          <input value={reference} onChange={(e) => setReference(e.target.value)} required maxLength={80} style={input} />
        </Field>
        <div style={{ display: "flex", gap: 12 }}>
          <Field label={t("admin.procurement.contracts.field.culture")}>
            <input value={culture} onChange={(e) => setCulture(e.target.value)} maxLength={40} style={input} />
          </Field>
          <Field label={t("admin.procurement.contracts.field.harvestYear")} required>
            <input type="number" value={harvestYear} onChange={(e) => setHarvestYear(e.target.value)} required style={input} />
          </Field>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <Field label={t("admin.procurement.contracts.field.quantityT")} required>
            <input type="number" min="0" step="0.001" value={quantityT} onChange={(e) => setQuantityT(e.target.value)} required style={input} />
          </Field>
          <Field label={t("admin.procurement.contracts.field.pricePerT")}>
            <input type="number" min="0" step="0.01" value={pricePerT} onChange={(e) => setPricePerT(e.target.value)} style={input} />
          </Field>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <Field label={t("admin.procurement.contracts.field.effectiveDate")}>
            <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} style={input} />
          </Field>
          <Field label={t("admin.procurement.contracts.field.expiresAt")}>
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} style={input} />
          </Field>
        </div>
        <Actions onClose={onClose} submitting={submitting} />
      </form>
    </Modal>
  );
}

function ContractDetailModal({
  contract, onClose, onChanged,
}: {
  contract: ProcurementContract;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  // Director approval gate: only ADMIN/SUPERADMIN may
  // put a draft contract in force — mirror of the server-side check.
  const { hasRole, hasPermission } = useAuth();
  const canWrite = hasPermission("section:procurement_contracts:write");
  const canFull = hasPermission("section:procurement_contracts:full");
  const isDirector = canFull && (hasRole("ADMIN") || hasRole("SUPERADMIN"));
  const [quantityT, setQuantityT] = useState(String(contract.quantityT));
  const [pricePerT, setPricePerT] = useState(contract.pricePerT != null ? String(contract.pricePerT) : "");
  const [harvestYear, setHarvestYear] = useState(String(contract.harvestYear));
  const [effective, setEffective] = useState(contract.effectiveDate ?? "");
  const [expires, setExpires] = useState(contract.expiresAt ?? "");
  const [busy, setBusy] = useState(false);

  // A colleague's supplier is read-only here: the server would refuse every write.
  const editable = canWrite && contract.canAct && (contract.status === "DRAFT" || contract.status === "ACTIVE");
  // Once in force, price and quantity are the directors' to change — the
  // referent keeps the dates. Mirror of the server-side CONTRACT_PRICE_LOCKED rule.
  const termsLocked = contract.status === "ACTIVE" && !isDirector;

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await procurementContractsApi.update(contract.id, {
        ...(termsLocked
          ? {}
          : { quantityT: Number(quantityT), pricePerT: pricePerT ? Number(pricePerT) : undefined }),
        harvestYear: Number(harvestYear),
        effectiveDate: effective || undefined,
        expiresAt: expires || undefined,
      });
      toast.success(t("admin.procurement.contracts.toast.updated"));
      await onChanged();
    } catch (err) {
      const e = err as ApiError;
      toast.error(e.code === "CONTRACT_PRICE_LOCKED"
        ? t("admin.procurement.contracts.priceLocked", "Only a director changes the price or quantity of an active contract.")
        : e.message);
    } finally {
      setBusy(false);
    }
  }

  async function transition(to: ProcurementContractStatus) {
    setBusy(true);
    try {
      await procurementContractsApi.transition(contract.id, to);
      toast.success(t("admin.procurement.contracts.toast.movedTo", { status: t(`procurement.contracts.status.${to.toLowerCase()}`) }));
      await onChanged();
      onClose();
    } catch (err) {
      const e = err as ApiError;
      toast.error(e.code === "CONTRACT_APPROVAL_REQUIRED"
        ? t("admin.procurement.contracts.approvalRequired", "Only a director may activate a procurement contract.")
        : e.message);
    } finally {
      setBusy(false);
    }
  }

  const allowed = canWrite && contract.canAct
    ? nextAllowed(contract.status).filter((s) => (s !== "ACTIVE" || isDirector) && (s !== "CANCELLED" || canFull))
    : [];

  return (
    <Modal onClose={onClose}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>
          {contract.referenceNumber} — {contract.companyName}
        </h2>
        <StatusBadge status={contract.status} />
      </header>

      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <Field label={t("admin.procurement.contracts.field.quantityT")} required>
            <input type="number" min="0" step="0.001" value={quantityT} onChange={(e) => setQuantityT(e.target.value)} required disabled={!editable || termsLocked} style={input} />
          </Field>
          <Field label={t("admin.procurement.contracts.field.pricePerT")}>
            <input type="number" min="0" step="0.01" value={pricePerT} onChange={(e) => setPricePerT(e.target.value)} disabled={!editable || termsLocked} style={input} />
          </Field>
          <Field label={t("admin.procurement.contracts.field.harvestYear")} required>
            <input type="number" value={harvestYear} onChange={(e) => setHarvestYear(e.target.value)} required disabled={!editable} style={input} />
          </Field>
        </div>
        {termsLocked && (
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 12.5 }}>
            {t("admin.procurement.contracts.termsLocked", "The contract is in force — a director changes its price and quantity.")}
          </p>
        )}
        <div style={{ display: "flex", gap: 12 }}>
          <Field label={t("admin.procurement.contracts.field.effectiveDate")}>
            <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} disabled={!editable} style={input} />
          </Field>
          <Field label={t("admin.procurement.contracts.field.expiresAt")}>
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} disabled={!editable} style={input} />
          </Field>
        </div>
        {editable && (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={secondary}>{t("action.cancel")}</button>
            <button type="submit" disabled={busy} style={primary}>{busy ? t("admin.procurement.contracts.action.saving") : t("action.save")}</button>
          </div>
        )}
      </form>

      {contract.status === "DRAFT" && !isDirector && (
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 12.5 }}>
          {t("admin.procurement.contracts.awaitingDirector", "A director activates this contract — the directors have been notified that it awaits approval.")}
        </p>
      )}

      {allowed.length > 0 && (
        <section>
          <h3 style={subTitle}>{t("admin.procurement.contracts.lifecycle")}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {allowed.map((to) => (
              <button type="button" key={to} onClick={() => transition(to)} disabled={busy} style={transitionBtn}>
                {t("admin.procurement.contracts.transitionTo", { status: t(`procurement.contracts.status.${to.toLowerCase()}`) })}
              </button>
            ))}
          </div>
        </section>
      )}
    </Modal>
  );
}

function nextAllowed(from: ProcurementContractStatus): ProcurementContractStatus[] {
  switch (from) {
    case "DRAFT":  return ["ACTIVE", "CANCELLED"];
    case "ACTIVE": return ["FULFILLED", "CANCELLED"];
    default:       return [];
  }
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>{children}</div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, flex: 1 }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

function Actions({ onClose, submitting }: { onClose: () => void; submitting: boolean }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
      <button type="button" onClick={onClose} style={secondary}>{t("action.cancel")}</button>
      <button type="submit" disabled={submitting} style={primary}>{submitting ? t("admin.procurement.contracts.action.saving") : t("action.save")}</button>
    </div>
  );
}

const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-1)" };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14, width: "100%" };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const transitionBtn: React.CSSProperties = { padding: "6px 12px", background: "var(--tone-indigo-bg)", color: "var(--tone-indigo)", border: "1px solid var(--tone-indigo)", borderRadius: 6, fontSize: 12, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", fontSize: 12 };
const subTitle: React.CSSProperties = { margin: "16px 0 4px", fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 720, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14, maxHeight: "90vh", overflowY: "auto" };
